import React, { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { ChevronDown, Menu as MenuIcon, Search, Building2 } from "lucide-react";
import history from "../../history";
import { usePageHeader } from "./PageHeaderContext.js";

/** Acha o rótulo do menu correspondente à rota atual (fallback de título). */
function deriveTitle(sections, pathname) {
  const p = (pathname || "").toLowerCase();
  for (const g of sections) {
    if (g.href && g.href.toLowerCase() === p) return g.label;
    for (const it of g.items || []) {
      if (it.to && it.to.toLowerCase() === p) return it.label;
    }
  }
  return "";
}

/** Lista achatada de itens navegáveis (para a busca rápida), com seção e ícone. */
function flattenItems(sections) {
  const list = [];
  sections.forEach((g) => {
    if (g.href) {
      list.push({ key: g.key, label: g.label, to: g.href, group: "Geral", Icon: g.GroupIcon });
    }
    (g.items || []).forEach((it) =>
      list.push({ key: it.key, label: it.label, to: it.to, group: g.label, Icon: it.Icon })
    );
  });
  return list;
}

/**
 * Topbar do shell (modelo Zendenta): título da página à esquerda, busca rápida
 * ao centro e o controle de conta/unidade à direita (abre o UnitPanel, que já
 * traz troca de unidade e logout).
 */
export default function AppTopBar({
  sections = [],
  unitName = "—",
  unitPanelOpen,
  onUnitClick,
  onMenuToggle,
}) {
  const location = useLocation();
  const { header } = usePageHeader();

  const matchesPath = header.path === location.pathname;
  const title =
    (matchesPath && header.title) || deriveTitle(sections, location.pathname) || "Fatura WEB";
  const description = matchesPath ? header.description : "";

  const items = useMemo(() => flattenItems(sections), [sections]);
  const [query, setQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => it.label.toLowerCase().includes(q));
  }, [items, query]);

  // Agrupa os resultados pela seção do menu (Financeiro, Cadastros, …).
  const grouped = useMemo(() => {
    const map = new Map();
    results.forEach((it) => {
      const k = it.group || "Páginas";
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(it);
    });
    return Array.from(map.entries());
  }, [results]);

  const goTo = (to) => {
    setQuery("");
    setSearchOpen(false);
    if (to) history.push(to);
  };

  return (
    <div className="app-header">
      <header className="app-topbar">
        <div className="app-topbar__left">
          <button
            type="button"
            className="app-topbar__menu"
            onClick={onMenuToggle}
            aria-label="Alternar menu"
            title="Alternar menu"
          >
            <MenuIcon size={18} />
          </button>

          <div className="app-topbar__pagetitle">
            <strong>{title}</strong>
            {description ? <span>{description}</span> : null}
          </div>
        </div>

        <div className="app-topbar__center">
          <div className="app-topbar__qs">
            <div className="app-topbar__qs-field">
              <Search size={16} />
              <input
                type="text"
                className="app-topbar__qs-input"
                placeholder="Buscar páginas…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={() => setSearchOpen(true)}
                onBlur={() => setSearchOpen(false)}
                aria-label="Buscar página"
              />
            </div>

            {searchOpen && (
              <div className="app-topbar__qs-menu" role="listbox">
                {results.length === 0 ? (
                  <div className="app-topbar__qs-empty">Nenhuma página encontrada.</div>
                ) : (
                  grouped.map(([groupLabel, groupItems]) => (
                    <div className="app-topbar__qs-group" key={groupLabel}>
                      <div className="app-topbar__qs-section">{groupLabel}</div>
                      {groupItems.map((it) => {
                        const ItemIcon = it.Icon || Search;
                        return (
                          <button
                            key={it.key || it.to}
                            type="button"
                            className="app-topbar__qs-item"
                            // mousedown dispara antes do blur do input
                            onMouseDown={(e) => {
                              e.preventDefault();
                              goTo(it.to);
                            }}
                          >
                            <ItemIcon size={15} strokeWidth={2} />
                            {it.label}
                          </button>
                        );
                      })}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        <div className="app-topbar__right">
          <button
            type="button"
            className="app-topbar__account"
            onClick={onUnitClick}
            aria-expanded={unitPanelOpen}
            aria-controls="app-unit-panel"
            title={`${unitName} — Trocar unidade`}
          >
            <span className="app-topbar__account-av" aria-hidden>
              <Building2 size={16} strokeWidth={2.25} />
            </span>
            <span className="app-topbar__account-info">
              <span className="app-topbar__account-unit">{unitName}</span>
              <span className="app-topbar__account-sub">Trocar unidade</span>
            </span>
            <ChevronDown size={16} className="app-topbar__account-chev" />
          </button>
        </div>
      </header>
    </div>
  );
}
